import React, { memo, useCallback, useMemo, useState } from 'react'
import Modal from '@mui/material/Modal'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { MCOnboarding } from 'src/interfaces/mcOnboarding'
import { Button } from '../shared/Button'
import { CircleCancelMajor, SendMajor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { handleApiError } from 'src/utils/errorHandlers'
import { Typography } from '@mui/material'
import { DataRow } from '../shared/DataRow'
import { MCStatusChip } from '../mc-list/shared/MCStatusChip'
import { MCStatus } from 'src/constants/mc/enums'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { RejectMCInvite, ResendMCInvite } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'

interface Props {
  mcOnboarding: MCOnboarding
  onClose(): void
  onRefresh(): Promise<void> | void
}

const MCInviteDetailsModalComponent = ({ mcOnboarding, onClose, onRefresh }: Props) => {
  const dispatch = useDispatch()

  const container = useMemo(() => document.getElementById('root'), [])

  const [isResendLoading, setIsResendLoading] = useState(false)
  const [isRejectLoading, setIsRejectLoading] = useState(false)

  const handleResendInvite = useCallback(async () => {
    setIsResendLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        ResendMCInvite.request({
          inviteId: mcOnboarding.inviteId,
        }),
      )

      showToast("The reminder has been resent to the master contractor's email address!")

      onRefresh()
    } catch (error) {
      handleApiError(error)
    }

    setIsResendLoading(false)
  }, [mcOnboarding.masterContractorId, onRefresh])

  const handleReject = useCallback(async () => {
    setIsRejectLoading(true)

    try {
      await createAsyncAction(
        dispatch,
        RejectMCInvite.request({
          inviteId: mcOnboarding.inviteId,
        }),
      )

      onClose()
      onRefresh()
    } catch (error) {
      handleApiError(error)
    }

    setIsRejectLoading(false)
  }, [mcOnboarding.masterContractorId, onRefresh, onClose])

  return (
    <Modal
      container={container}
      open
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
      css={tw`flex flex-row w-full h-full`}
    >
      <>
        <div css={tw`w-[400px] h-full bg-white flex flex-col`}>
          <ModalHeader
            title="Master contractor details"
            onClose={onClose}
            className={tw`text-black h-[48px]`}
          />
          <div css={tw`flex h-[calc(100vh-48px)]`}>
            <div
              css={tw`max-w-[400px] flex-1 flex flex-col justify-between border-0 border-r border-solid border-r-[#EDEDED] px-4`}
            >
              <div>
                <Typography
                  css={tw`py-4 border-0 border-b border-solid border-b-[#EDEDED]`}
                  variant="h2"
                >
                  {mcOnboarding.companyName}
                </Typography>
                <DataRow title="Status" component={<MCStatusChip status={MCStatus.Invited} />} />
                <DataRow title="Contact name" text={mcOnboarding.ownerName} />
                <DataRow title="Email" text={mcOnboarding.ownerEmail} />
              </div>
              <div css={tw`py-4 border-0 border-t border-solid border-[#EDEDED]`}>
                <Button
                  loading={isResendLoading}
                  color="light"
                  size="small"
                  className={tw`w-full mb-2`}
                  rightIcon={<SendMajor width={20} fill={colors.PRIMARY_PURPLE_500} />}
                  onClick={handleResendInvite}
                >
                  Resend an invite
                </Button>
                <Button
                  loading={isRejectLoading}
                  color="grey"
                  size="small"
                  className={tw`w-full`}
                  rightIcon={<CircleCancelMajor width={20} />}
                  onClick={handleReject}
                >
                  Reject master contractor
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    </Modal>
  )
}

export const MCInviteDetailsModal = memo(MCInviteDetailsModalComponent)
